import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  type UserCredential,
} from "firebase/auth";
import { getAnalytics, logEvent as fbLogEvent, setUserId, setUserProperties, type Analytics } from "firebase/analytics";
import { getConsent } from "./cookieConsent";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Only initialize Firebase if API key is configured
const firebaseEnabled = Boolean(firebaseConfig.apiKey);
const app = firebaseEnabled ? initializeApp(firebaseConfig) : null;
export const auth = app ? getAuth(app) : null;

// ── Analytics ────────────────────────────────────────────────────────
// Analytics requires measurementId (G-XXXXXXXXXX) to send data to Firebase
let analytics: Analytics | null = null;
if (app && firebaseConfig.measurementId && typeof window !== "undefined") {
  try {
    analytics = getAnalytics(app);
  } catch (e) {
    console.warn("Firebase Analytics could not be initialized:", e);
  }
}
export { analytics };

/**
 * Log a custom event to Firebase Analytics — gated by the user's
 * KVKK / GDPR analytics consent. If the user has rejected analytics
 * (or hasn't decided yet), the call silently no-ops. This protects
 * the platform from sending behavioural data before consent and
 * aligns with KVKK Art. 5(1) (explicit consent required for non-
 * essential processing).
 */
export function logEvent(eventName: string, params?: Record<string, unknown>) {
  if (!analytics) return;
  if (!getConsent().analytics) return;
  fbLogEvent(analytics, eventName, params);
}

/**
 * Identify the current user for Analytics — also gated by consent.
 * Without analytics consent we do not associate a userId with the
 * analytics stream at all (no shadow profile).
 */
export function identifyUser(userId: string, properties?: Record<string, string>) {
  if (!analytics) return;
  if (!getConsent().analytics) return;
  setUserId(analytics, userId);
  if (properties) {
    setUserProperties(analytics, properties);
  }
}

// Providers
const googleProvider = new GoogleAuthProvider();

function requireAuth() {
  if (!auth) throw new Error("Firebase is not configured. Set VITE_FIREBASE_API_KEY in .env");
  return auth;
}

export async function firebaseSignInWithGoogle(): Promise<UserCredential> {
  return signInWithPopup(requireAuth(), googleProvider);
}

export async function firebaseSignInWithEmail(
  email: string,
  password: string
): Promise<UserCredential> {
  return signInWithEmailAndPassword(requireAuth(), email, password);
}

export async function firebaseCreateUser(
  email: string,
  password: string,
  displayName: string
): Promise<UserCredential> {
  const a = requireAuth();
  const cred = await createUserWithEmailAndPassword(a, email, password);
  await updateProfile(cred.user, { displayName });
  return cred;
}

export async function getFirebaseIdToken(): Promise<string> {
  const a = requireAuth();
  const user = a.currentUser;
  if (!user) throw new Error("No Firebase user signed in");
  return user.getIdToken();
}

export async function firebaseResetPassword(email: string): Promise<void> {
  return sendPasswordResetEmail(requireAuth(), email);
}


// ════════════════════════════════════════════════════════════════════
// TOTP Multi-Factor Authentication (Authenticator app, e.g. Google
// Authenticator / Authy). Requires Firebase Blaze plan + MFA enabled in
// the project's Authentication settings. Falls back to a clear error
// when MFA isn't provisioned so the UI can show a helpful message
// rather than a generic crash.
// ════════════════════════════════════════════════════════════════════

import {
  multiFactor,
  TotpMultiFactorGenerator,
  TotpSecret,
  getMultiFactorResolver,
  type MultiFactorResolver,
  type MultiFactorError,
} from "firebase/auth";

/** Status of MFA enrolment for the currently signed-in Firebase user. */
export function getMfaStatus(): { enrolled: boolean; factorCount: number } {
  if (!auth?.currentUser) return { enrolled: false, factorCount: 0 };
  const factors = multiFactor(auth.currentUser).enrolledFactors;
  return { enrolled: factors.length > 0, factorCount: factors.length };
}

/** Step 1 of enrolment: ask Firebase for a TOTP secret. Returns the
 *  secret + an otpauth URL the user can scan into Authenticator.
 *  The user's Firebase session must be RECENT (within last few mins);
 *  if not, the caller must re-authenticate first.
 */
export async function startTotpEnrolment(): Promise<{
  secret: TotpSecret;
  qrCodeUrl: string;
  secretKey: string;
}> {
  const a = requireAuth();
  if (!a.currentUser) throw new Error("Not signed in");

  const session = await multiFactor(a.currentUser).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);

  // FQDN that appears in the user's Authenticator app entry.
  const accountName = a.currentUser.email || a.currentUser.uid;
  const issuer = "Play&Trade";
  const qrCodeUrl = secret.generateQrCodeUrl(accountName, issuer);

  return { secret, qrCodeUrl, secretKey: secret.secretKey };
}

/** Step 2 of enrolment: user typed a 6-digit code from Authenticator,
 *  prove they have the device. Display name shows up on the user's
 *  factor list (so they can name it "Phone", "Backup", etc.).
 */
export async function completeTotpEnrolment(
  secret: TotpSecret,
  code: string,
  displayName: string = "Authenticator",
): Promise<void> {
  const a = requireAuth();
  if (!a.currentUser) throw new Error("Not signed in");
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
  await multiFactor(a.currentUser).enroll(assertion, displayName);
}

/** Remove ALL enrolled TOTP factors. The user must re-enrol to use 2FA again. */
export async function unenrollAllMfa(): Promise<void> {
  const a = requireAuth();
  if (!a.currentUser) throw new Error("Not signed in");
  const mf = multiFactor(a.currentUser);
  for (const factor of mf.enrolledFactors) {
    await mf.unenroll(factor);
  }
}

/** Login-time MFA challenge resolver — call from the catch block when
 *  signIn throws `auth/multi-factor-auth-required`.
 *
 *  Pass the original error; this returns the resolver. The UI then
 *  picks a hint (typically the only TOTP hint), prompts for the 6-digit
 *  code, and calls completeMfaSignIn(resolver, hint, code).
 */
export function getMfaResolverForError(err: unknown): MultiFactorResolver | null {
  const a = auth;
  if (!a) return null;
  if (typeof err === "object" && err !== null && "code" in err) {
    const e = err as MultiFactorError;
    if (e.code === "auth/multi-factor-auth-required") {
      return getMultiFactorResolver(a, e);
    }
  }
  return null;
}

/** Complete the MFA sign-in by submitting the TOTP code for a hint. */
export async function completeMfaSignIn(
  resolver: MultiFactorResolver,
  hintIndex: number,
  code: string,
): Promise<UserCredential> {
  const hint = resolver.hints[hintIndex];
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code);
  return resolver.resolveSignIn(assertion);
}
